import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  customerService,
  GetCustomersParams,
  CreateCustomerRequest,
  UpdateCustomerRequest,
} from "../api/services/customers/customerService";
import { CreateCustomerFormData } from "../types/customer";
import {
  documentService,
  UpdateDocumentRequest,
} from "../api/services/documents/documentService";

const CUSTOMERS_STALE_TIME =
  Number(process.env.REACT_APP_CUSTOMERS_STALE_TIME) || 5 * 60 * 1000;

const COUNTRIES_STALE_TIME =
  Number(process.env.REACT_APP_COUNTRIES_STALE_TIME) || 60 * 60 * 1000;

// Query keys
export const customerKeys = {
  all: ["customers"] as const,
  lists: () => [...customerKeys.all, "list"] as const,
  list: (params: GetCustomersParams) =>
    [...customerKeys.lists(), params] as const,
  details: () => [...customerKeys.all, "detail"] as const,
  detail: (id: string) => [...customerKeys.details(), id] as const,
};

// Hook for getting customers with pagination and filters
export const useCustomers = (params: GetCustomersParams = {}) => {
  return useQuery({
    queryKey: customerKeys.list(params),
    queryFn: () => customerService.getCustomers(params),
    staleTime: CUSTOMERS_STALE_TIME,
  });
};

// Hook for getting a single customer
export const useCustomer = (id: string) => {
  return useQuery({
    queryKey: customerKeys.detail(id),
    queryFn: () => customerService.getCustomer(id),
    enabled: !!id,
    staleTime: CUSTOMERS_STALE_TIME,
  });
};

// Hook for creating a customer
export const useCreateCustomer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCustomerRequest) =>
      customerService.createCustomer(data),
    onSuccess: () => {
      // Invalidate and refetch customers list
      queryClient.invalidateQueries({ queryKey: customerKeys.lists() });
    },
  });
};

// Hook for updating a customer
export const useUpdateCustomer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      customerId,
      data,
    }: {
      customerId: string;
      data: UpdateCustomerRequest;
    }) => customerService.updateCustomer(customerId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customers"] });
    },
  });
};

export const useUpdateCustomerPhoto = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      customerId,
      photoFile,
    }: {
      customerId: string;
      photoFile: File;
    }) => customerService.updateCustomerPhoto(customerId, photoFile),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customers"] });
    },
  });
};

export const useUpdateDocument = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      documentId,
      data,
    }: {
      documentId: string;
      data: UpdateDocumentRequest;
    }) => documentService.updateDocument(documentId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["documents"] });
      queryClient.invalidateQueries({ queryKey: ["customers"] });
    },
  });
};

export const useCustomerDocuments = (customerId: string) => {
  return useQuery({
    queryKey: ["documents", customerId],
    queryFn: () => documentService.getCustomerDocuments(customerId),
    enabled: !!customerId,
  });
};

// Hook for deleting a customer
export const useDeleteCustomer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => customerService.deleteCustomer(id),
    onSuccess: (_, deletedId) => {
      // Remove the customer from cache
      queryClient.removeQueries({ queryKey: customerKeys.detail(deletedId) });
      // Invalidate customers list
      queryClient.invalidateQueries({ queryKey: customerKeys.lists() });
    },
  });
};

// Hook for getting countries (useful for forms)
export const useCustomerCountries = () => {
  return useQuery({
    queryKey: ["countries"],
    queryFn: () => customerService.getCountries(),
    staleTime: COUNTRIES_STALE_TIME,
  });
};

export const useCreateCustomerWithFiles = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCustomerFormData) =>
      customerService.createCustomerWithFiles(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customerKeys.lists() });
    },
    onError: (error) => {
      console.error("Error creating customer:", error);
    },
  });
};

// Custom hook for customer management with common operations
export const useCustomerManagement = (
  initialParams: GetCustomersParams = {},
) => {
  const queryClient = useQueryClient();

  const customersQuery = useCustomers(initialParams);
  const createMutation = useCreateCustomer();
  const updateMutation = useUpdateCustomer();
  const deleteMutation = useDeleteCustomer();
  const countriesQuery = useCustomerCountries();

  const refreshCustomers = () => {
    queryClient.invalidateQueries({ queryKey: customerKeys.lists() });
  };

  const clearCache = () => {
    queryClient.clear();
    customerService.clearCountriesCache();
  };

  return {
    customers: customersQuery.data || {
      items: [],
      pagination: { total: 0, skip: 0, limit: 0, returned: 0 },
      filtersApplied: {
        name: null,
        email: null,
        phone: null,
        customerType: null,
        countryId: null,
      },
    },
    isLoading: customersQuery.isLoading,
    isError: customersQuery.isError,
    error: customersQuery.error,
    countries: countriesQuery.data || [],
    isCountriesLoading: countriesQuery.isLoading,

    // Mutations
    createCustomer: createMutation.mutate,
    updateCustomer: updateMutation.mutate,
    deleteCustomer: deleteMutation.mutate,

    // Mutation states
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,

    // Mutation errors
    createError: createMutation.error,
    updateError: updateMutation.error,
    deleteError: deleteMutation.error,

    // Utility functions
    refreshCustomers,
    clearCache,

    // Customer types for forms
    customerTypes: customerService.getCustomerTypes(),
  };
};
